import { useTranslation } from 'react-i18next';
import { Icon, type IconName } from '@/components/common/Icon';
import { Card } from '@/components/ui/card';
import type { ReportsSummary } from '@/api/reports/reportsApi.types';

interface KpiTilesProps {
  summary: ReportsSummary | undefined;
  isLoading: boolean;
}

interface KpiTileDefinition {
  id: string;
  iconName: IconName;
  labelKey: string;
  value: number | null;
}

export function KpiTiles({ summary, isLoading }: KpiTilesProps) {
  const tiles: KpiTileDefinition[] = [
    {
      id: 'totalVisits',
      iconName: 'Calendar',
      labelKey: 'reports:kpi.totalVisits',
      value: summary === undefined ? null : summary.totalVisits,
    },
    {
      id: 'completedVisits',
      iconName: 'CheckCircle',
      labelKey: 'reports:kpi.completedVisits',
      value: summary === undefined ? null : summary.completedVisits,
    },
    {
      id: 'cancelledVisits',
      iconName: 'AlertCircle',
      labelKey: 'reports:kpi.cancelledVisits',
      value: summary === undefined ? null : summary.cancelledVisits,
    },
    {
      id: 'activeClients',
      iconName: 'Users',
      labelKey: 'reports:kpi.activeClients',
      value: summary === undefined ? null : summary.activeClients,
    },
    {
      id: 'summariesShared',
      iconName: 'Send',
      labelKey: 'reports:kpi.summariesShared',
      value: summary === undefined ? null : summary.summariesShared,
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
      {tiles.map(function renderTile(tile) {
        return <KpiTile key={tile.id} definition={tile} isLoading={isLoading} />;
      })}
    </div>
  );
}

function KpiTile({
  definition,
  isLoading,
}: {
  definition: KpiTileDefinition;
  isLoading: boolean;
}) {
  const { t } = useTranslation('reports');
  return (
    <Card className="flex items-center gap-4">
      <span className="w-11 h-11 rounded-xl bg-zoovpalelavender text-zooprimary flex items-center justify-center shrink-0">
        <Icon name={definition.iconName} />
      </span>
      <div className="min-w-0">
        <div className="text-2xl font-bold text-zooindigo leading-none">
          {isLoading === true || definition.value === null ? '—' : definition.value}
        </div>
        <div className="text-xs text-zoomuted mt-1 truncate">{t(definition.labelKey)}</div>
      </div>
    </Card>
  );
}
